import { Typography, Box, Paper, Stack, Grid } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

const tryParseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const formatLabel = (str) =>
  str
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (match) => match.toUpperCase());

const renderValue = (value) => {
  if (value === null || value === undefined) return "N/A";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

function PackCard({ pack }) {
  const entries = Object.entries(pack).filter(
    ([, v]) => typeof v !== "object" || v === null
  );

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        height: "100%",
        py: 1.5,
        px: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "background.paper",
        "&:hover": {
          borderColor: "primary.main",
          backgroundColor: "action.hover",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
          gap: 0.5,
        }}
      >
        <Inventory2OutlinedIcon
          sx={{ fontSize: "0.9rem", color: "primary.main" }}
        />
        <Typography
          variant="subtitle1"
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            textAlign: "center",
            textTransform: "uppercase",
            color: "primary.main",
          }}
        >
          {pack.name ?? pack.packName ?? pack.type ?? `Pack`}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {entries
          .filter(([k]) => k !== "name" && k !== "packName" && k !== "type")
          .map(([key, value]) => (
            <Box
              key={key}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 0.5,
                py: 0.25,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                  flex: 1,
                }}
              >
                {formatLabel(key)}:
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  fontSize: "0.75rem",
                  textAlign: "right",
                  flex: 1,
                }}
              >
                {renderValue(value)}
              </Typography>
            </Box>
          ))}
      </Box>
    </Paper>
  );
}

function ObjectView({ data }) {
  const entries = Object.entries(data);
  return (
    <Stack spacing={0.5} sx={{ p: 1, flex: 1, justifyContent: "center" }}>
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {formatLabel(key)}:
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              fontSize: "0.75rem",
            }}
          >
            {renderValue(value)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export default function MarketplacePackSettings({ rawData }) {
  const parsed = tryParseJson(rawData);

  if (!parsed) {
    return (
      <Typography color="error" variant="body2" sx={{ p: 2 }}>
        Invalid or empty Marketplace Pack Settings JSON
      </Typography>
    );
  }

  if (Array.isArray(parsed)) {
    return (
      <Stack spacing={1} sx={{ p: 0, flex: 1, justifyContent: "center" }}>
        <Grid container>
          {parsed.map((pack, index) => (
            <Grid item xs={Math.max(2, Math.floor(12 / parsed.length))} key={index} sx={{ p: 1 }}>
              <PackCard pack={pack} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  return (
    <Stack spacing={1} sx={{ p: 0, flex: 1, justifyContent: "center" }}>
      <ObjectView data={parsed} />
    </Stack>
  );
}
